import { type ReactNode, useEffect, useMemo } from "react"

import {
  createDashboardStateStore,
  DashboardStateContext,
  type DashboardStateStore,
} from "./dashboard-state"

interface DashboardStateProviderProps {
  initialState?: Record<string, unknown>
  children: ReactNode
  /**
   * Invoked with a getter for the live store state. Lets callers outside
   * the provider (e.g. the refresh button in the toolbar) read current
   * state without needing to be a descendant. The effect re-fires when the
   * callback identity changes, so memoise it in the parent for stability.
   */
  onStateReady?: (getState: () => Record<string, unknown>) => void
  /**
   * Invoked with the live state store so callers outside the provider
   * (e.g. the bookmark menu's "apply view" action) can both read AND
   * write. Stable identity matches `onStateReady`; the parent should
   * memoise.
   */
  onStoreReady?: (store: DashboardStateStore) => void
  /**
   * §2.2 — Pluggable state store. Pass a caller-owned store
   * (typically backed by an app-wide Zustand/Redux/Jotai/XState
   * implementation via createStoreAdapter) to share dashboard state
   * with the rest of the app. When omitted the provider creates a
   * fresh in-memory store, matching the original behaviour.
   *
   * Stable identity — the provider doesn't re-create or wrap the
   * passed store, so subscriptions registered before mount keep
   * firing.
   */
  store?: DashboardStateStore
}

export function DashboardStateProvider({
  initialState,
  children,
  onStateReady,
  onStoreReady,
  store: externalStore,
}: DashboardStateProviderProps) {
  // Default to a fresh in-memory store when none is supplied. The
  // useMemo dependency includes the external store so swapping
  // providers (e.g. swapping tenants) builds a new internal store.
  const store = useMemo(() => externalStore ?? createDashboardStateStore(), [externalStore])

  useEffect(() => {
    if (initialState) {
      store.initDashState(initialState)
    }
  }, [initialState, store])

  useEffect(() => {
    if (!onStateReady) return
    try {
      onStateReady(store.getState)
    } catch (err) {
      // Swallow user-callback errors here — letting them bubble would
      // unmount the dashboard tree. Logged with the standard `[dash]`
      // prefix so it lines up with the other dashboard runtime
      // diagnostics (watch-runtime, action-observer, etc.) when an
      // operator greps the console.
      console.error("[dash] onStateReady callback threw:", err)
    }
  }, [onStateReady, store])

  useEffect(() => {
    if (!onStoreReady) return
    try {
      onStoreReady(store)
    } catch (err) {
      console.error("[dash] onStoreReady callback threw:", err)
    }
  }, [onStoreReady, store])

  return <DashboardStateContext.Provider value={store}>{children}</DashboardStateContext.Provider>
}
